import { useSyncExternalStore } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  company: string;
  country: string;
  phone: string;
  username: string;
}

interface AuthState {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  loading: boolean;
}

// ---- Shared singleton store: subscribe once, share across all consumers ----
let state: AuthState = { user: null, session: null, profile: null, loading: true };
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());
const setState = (patch: Partial<AuthState>) => {
  state = { ...state, ...patch };
  emit();
};

let initialized = false;
let inflightProfileUserId: string | null = null;

const fetchProfile = async (userId: string) => {
  if (inflightProfileUserId === userId) return;
  inflightProfileUserId = userId;
  try {
    const { data } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .maybeSingle();
    if (state.user?.id === userId) {
      setState({ profile: (data as Profile | null) ?? null });
    }
  } finally {
    if (inflightProfileUserId === userId) inflightProfileUserId = null;
  }
};

const initAuth = () => {
  if (initialized) return;
  initialized = true;

  supabase.auth.onAuthStateChange((_event, session) => {
    const prevUserId = state.user?.id ?? null;
    const nextUserId = session?.user?.id ?? null;
    setState({
      session,
      user: session?.user ?? null,
      profile: nextUserId === prevUserId ? state.profile : null,
      loading: false,
    });
    if (nextUserId && nextUserId !== prevUserId) {
      fetchProfile(nextUserId);
    }
  });

  supabase.auth.getSession().then(({ data: { session } }) => {
    setState({
      session,
      user: session?.user ?? null,
      loading: false,
    });
    if (session?.user) fetchProfile(session.user.id);
  });
};

const subscribe = (cb: () => void) => {
  initAuth();
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};
const getSnapshot = () => state;

export function useAuth() {
  const snap = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  return { ...snap, signOut };
}

const signOut = async () => {
  await supabase.auth.signOut({ scope: "local" });
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith("sb-") || key.startsWith("supabase")) {
      localStorage.removeItem(key);
    }
  });
  setState({ user: null, session: null, profile: null });
};
